import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import omit from 'lodash.omit';

const propTypes = {
  element: PropTypes.shape({
    cardElement: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    cardElementConfiguration: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  localElement: PropTypes.shape({
    cardElement: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    cardElementConfiguration: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  setLocalElement: PropTypes.func.isRequired,
  cardElementBounds: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
  onCardElementUpdate: PropTypes.func.isRequired,
  className: PropTypes.string,
};

const CardDesignerCardElementDragControls = ({
  element,
  localElement,
  setLocalElement,
  cardElementBounds,
  onCardElementUpdate,
  className,
}) => {
  const { x: boundedX, y: boundedY } = cardElementBounds;
  const [minimumInitialPosition, setMinimumInitialPosition] = useState(null);
  const [initialPosition, setInitialPosition] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dragData, setDragData] = useState(null);
  const [draggedElement, setDraggedElement] = useState(false);
  const [initialMousePosition, setInitialMousePosition] = useState({});
  const { frame } = localElement.cardElement;

  useEffect(() => {
    const { x: xPercent, y: yPercent } = frame;
    const minX = Math.floor((boundedX * 0.001) / xPercent);
    const minY = Math.floor((boundedY * 0.001) / yPercent);
    setInitialPosition({ x: boundedX, y: boundedY });
    setMinimumInitialPosition({ x: minX, y: minY });
  }, [cardElementBounds]);

  useEffect(() => {
    const onDrag = event => {
      const { clientX, clientY } = event;
      const { x, y } = initialPosition;
      const { x: minX, y: minY } = minimumInitialPosition;
      const { x: initialClientX = clientX, y: initialClientY = clientY } =
        initialMousePosition;
      const {
        x: xPercent,
        y: yPercent,
        width: widthPercent,
        height: heightPercent,
      } = frame;

      let newX = Math.floor(x + clientX - initialClientX);
      let newY = Math.floor(y + clientY - initialClientY);

      // newX and newY should never be less or equal to than 0
      if (newX <= 0) {
        newX = minX;
      }
      if (newY <= 0) {
        newY = minY;
      }

      //  use Rule of 3 to determine the appropriate % value
      let newXPercent = parseFloat(
        ((newX * xPercent) / Math.max(x, 1)).toFixed(3),
      );
      let newYPercent = parseFloat(
        ((newY * yPercent) / Math.max(y, 1)).toFixed(3),
      );

      // Do not go below or equal to 0 since that messes up calculations
      if (newXPercent + widthPercent >= 0.999) {
        newXPercent = 0.999 - widthPercent;
      } else if (newXPercent <= 0.001) {
        newXPercent = 0.001;
      }
      if (newYPercent + heightPercent >= 0.999) {
        newYPercent = 0.999 - heightPercent;
      } else if (newYPercent <= 0.001) {
        newYPercent = 0.001;
      }

      setDraggedElement(true);
      setDragData({
        x: newXPercent,
        y: newYPercent,
      });
      setInitialPosition({ x: newX, y: newY });

      setLocalElement({
        ...localElement,
        cardElement: {
          ...localElement.cardElement,
          frame: {
            ...frame,
            x: newXPercent,
            y: newYPercent,
          },
        },
      });
    };

    const onStopDrag = () => {
      window.removeEventListener('mousemove', onDrag);
      setDragging(false);
    };

    if (dragging) {
      window.addEventListener('mousemove', onDrag);
      window.addEventListener('mouseup', onStopDrag);
    }
    return () => {
      window.removeEventListener('mousemove', onDrag);
      window.removeEventListener('mouseup', onStopDrag);
    };
  }, [dragging, boundedX, boundedY]);

  useEffect(() => {
    // We have finished dragging the element when the drag value resets.
    // If at said point, we have dragged the element, and have drag data,
    // update the objects accordingly.
    if (!dragging && draggedElement && dragData) {
      onCardElementUpdate({
        cardElement: element.cardElement,
        data: {
          frame: {
            ...omit(frame, '__typename'),
            ...dragData,
          },
        },
      });
      setDraggedElement(false);
      setDragData(null);
    }
  }, [dragging, draggedElement, dragData]);

  const classes = classNames({
    'card-designer-card-element-drag-controls': true,
    'cursor-grabbing': dragging,
    [className]: !!className,
  });

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Drag"
      className={classes}
      onMouseDown={event => {
        const { clientX, clientY } = event;
        setInitialMousePosition({ x: clientX, y: clientY });
        setDragging(true);
      }}
      onMouseUp={() => {
        setDragging(false);
      }}
    />
  );
};

CardDesignerCardElementDragControls.propTypes = propTypes;

export default CardDesignerCardElementDragControls;
